/**
 * Place Trust Report loader — the web map's read path into the truth engine.
 *
 * Endpoint: GET {ENGINE_BASE}/trust-report/place/{suburb name or place_id}
 * (pulse worker; CORS open, 5-min HTTP cache upstream, 24h report cache).
 *
 * Honesty contract (locked by the engine — mirror it, never soften it):
 *   * `coverage_status` 'partial_report'      → NEVER headline the numeric
 *     trust_score; show `score_label` instead.
 *   * 'substantial_report'                    → number allowed WITH coverage
 *     caveat copy.
 *   * pillar status 'partial_live'            → must render `coverage_label`
 *     + missing sub-signals so a score is never read as comprehensive.
 *   * absence of data is "not indexed yet", never "bad".
 *
 * Engine coverage today: Sydney only. Other cities short-circuit to null and
 * the truth block self-hides — no fake loading states for cities the engine
 * does not index yet.
 */

export type PillarStatus = "live" | "partial_live" | "not_yet_implemented" | "not_applicable";

export interface TrustPillar {
  score: number | null;
  status: PillarStatus;
  coverage_label?: string;
  missing_subsignals?: string[];
}

export interface TrustReportLite {
  place_id: string;
  suburb_name: string;
  trust_score: number | null;
  coverage_status: "partial_report" | "substantial_report" | "full_report";
  populated_pillars_count: number;
  score_label: string;
  trust_summary: string;
  pillars: Record<string, TrustPillar>;
  freshness?: { display?: string; label?: string };
  /** Place Brief lead — wire shape is { headline, bullets, confidence };
   *  `body` kept for forward-compat, plain string for legacy caches. */
  decision_summary?:
    | { headline?: string; body?: string; bullets?: string[]; confidence?: string }
    | string
    | null;
  disclaimer?: string;
}

/** Cities the engine currently indexes. Extend when city.status flips live. */
const ENGINE_CITIES = new Set(["syd"]);

const ENGINE_BASE =
  (globalThis as { HYPERLOCAL_ENGINE_BASE?: string }).HYPERLOCAL_ENGINE_BASE ??
  "https://hyperlocal-pulse.kuchipudi-kumarchowdary.workers.dev";

/** Resolve the engine city for a verdict id ('syd-bondi' → 'syd'; HYD ids are unprefixed). */
export function cityFromVerdictId(id: string): "syd" | "mel" | "hyd" | "blr" {
  const p = id.split("-")[0];
  if (p === "syd" || p === "mel" || p === "blr") return p;
  return "hyd";
}

/** True when the engine indexes the city. The truth block reserves its
 *  vertical slot only for these — other cities keep zero footprint. */
export function isEngineCity(city: string): boolean {
  return ENGINE_CITIES.has(city);
}

const cache = new Map<string, { at: number; report: TrustReportLite | null }>();
/** TTL for a good report. */
const TTL_MS = 5 * 60 * 1000;
/** TTL for a cached failure — long enough to stop a render loop from
 *  hammering a down endpoint, short enough that reopening the card a
 *  minute later actually retries. A transient fetch failure must never
 *  poison five minutes of session (gate-verdict residual, closed
 *  2026-06-13). */
const FAILURE_TTL_MS = 30 * 1000;

/**
 * Fetch the trust report for a suburb. Returns null when the city isn't
 * engine-indexed, the place is unknown, or the fetch fails — callers
 * render nothing rather than an error state. Failures are cached for
 * only 30s (vs 5min for good reports) and the retry bypasses the HTTP
 * cache so a stale browser-cached error can't satisfy it.
 */
export async function loadTrustReport(
  verdictId: string,
  suburbName: string,
): Promise<TrustReportLite | null> {
  const city = cityFromVerdictId(verdictId);
  if (!ENGINE_CITIES.has(city)) return null;

  const key = `${city}:${suburbName.toLowerCase()}`;
  const hit = cache.get(key);
  if (hit) {
    const ttl = hit.report !== null ? TTL_MS : FAILURE_TTL_MS;
    if (Date.now() - hit.at < ttl) return hit.report;
  }
  // A previous miss within the browser-HTTP-cache window (max-age=300)
  // would otherwise be re-served from disk — bypass it on retry.
  const retryingAfterFailure = hit !== undefined && hit.report === null;

  let report: TrustReportLite | null = null;
  try {
    const res = await fetch(
      `${ENGINE_BASE}/trust-report/place/${encodeURIComponent(suburbName)}`,
      {
        headers: { accept: "application/json" },
        cache: retryingAfterFailure ? "reload" : "default",
      },
    );
    if (res.ok) {
      const body = (await res.json()) as TrustReportLite;
      if (body && typeof body === "object" && body.pillars) report = body;
    }
  } catch {
    report = null; // offline / blocked — block self-hides
  }
  cache.set(key, { at: Date.now(), report });
  return report;
}

export function engineBase(): string {
  return ENGINE_BASE;
}
